import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { sections as sectionsApi, Section } from '../lib/api';
import { STEM_META, fmtTime, fmtRelativeDate } from '../lib/format';
import { STEM_COLOR } from '../components/studioIcons';
import { StemEngine, STEMS, StemName, MixState, DEFAULT_MIX } from '../lib/studioEngine';
import { loadSectionIndex, getSeparation, stemUrlsFrom } from '../lib/stemCache';
import './sections.css';

type SortMode = 'recent' | 'oldest' | 'az' | 'longest';
type ViewMode = 'grid' | 'list' | 'compact';

// ── helpers ──
function hashSeed(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
function makeBars(seed: number, n: number): number[] {
  const out: number[] = [];
  let x = (seed || 1) >>> 0;
  for (let i = 0; i < n; i++) {
    x = (x * 1103515245 + 12345) & 0x7fffffff;
    const r = x / 0x7fffffff;
    const env = 0.42 + 0.58 * Math.sin((i / (n - 1)) * Math.PI);
    const h = (0.18 + 0.82 * r) * 100 * env;
    out.push(Math.round(Math.max(9, Math.min(100, h))));
  }
  return out;
}
function rgba(hex: string, a: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}
function songName(s: Section): string {
  const v = s.video_id || '';
  if (s.source_kind === 'url' || /^https?:\/\//i.test(v)) return (s.title && s.title.trim()) || 'YouTube clip';
  const base = v.replace(/\.[a-z0-9]{1,5}$/i, '').replace(/_+/g, ' ').trim();
  return base || s.label || 'Untitled';
}

// YouTube helpers: the section's video_id is the YT id (or a YT url). Used to
// show the thumbnail and link back to the source video (the only place a
// YouTube-sourced clip can be re-separated — via the extension on YouTube).
function ytId(s: Section): string | null {
  if (s.source_kind !== 'url' && !/^https?:\/\//i.test(s.video_id || '')) return null;
  const v = s.video_id || '';
  const m =
    v.match(/(?:v=|youtu\.be\/|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{11})/) ||
    v.match(/^([A-Za-z0-9_-]{11})$/);
  return m ? m[1] : null;
}
function ytWatchUrl(s: Section): string | null {
  const id = ytId(s);
  if (!id) return null;
  const t = Math.max(0, Math.floor(s.start || 0));
  return `https://www.youtube.com/watch?v=${id}${t ? `&t=${t}s` : ''}`;
}
function ytThumb(s: Section): string | null {
  const id = ytId(s);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}
function openTab(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

// What's actually in the section: a single isolated stem, or a blend. The badge
// shows the stem when it's one part, else "Mix" (or a recognised preset) with a
// stack of dots for the stems you can hear.
interface MixInfo {
  label: string;
  dots: string[];
  textColor: string;
}
function mixInfo(s: Section): MixInfo {
  const vols = STEMS.map((st) => ({ st, v: s.stem_mix?.[st] ?? (st === (s.stem as StemName) ? 1 : 0) }));
  const audible = vols.filter((x) => x.v > 0.02).sort((a, b) => b.v - a.v);
  if (audible.length <= 1) {
    const st = audible[0]?.st ?? (s.stem as StemName);
    const c = STEM_COLOR[st] ?? 'var(--sec-accent)';
    return { label: STEM_META[st]?.label ?? st, dots: [c], textColor: c };
  }
  const dots = audible.slice(0, 4).map((x) => STEM_COLOR[x.st] ?? '#9a9bab');
  const vocals = vols.find((x) => x.st === 'vocals')?.v ?? 0;
  const others = audible.filter((x) => x.st !== 'vocals').length;
  let label = 'Mix';
  if (vocals <= 0.02 && others >= 2) label = 'Instrumental';
  else if (vocals > 0.02 && vocals < 0.4 && others >= 3) label = 'Karaoke';
  return { label, dots, textColor: 'var(--sec-text)' };
}

// ── view model per section ──
interface VM {
  s: Section;
  color: string;
  tint: string;
  stemLabel: string;
  mix: MixInfo;
  name: string;
  song: string;
  bars: number[];
  timeRange: string;
  clipLen: string;
  ago: string;
}

export function Sections() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Section[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortMode>('recent');
  // List is the default: a saved loop is identified by its label, song and
  // in/out point, and the list shows all three at once. The grid leads with
  // artwork, which is the least distinguishing thing about two loops cut from
  // the same video.
  const [view, setView] = useState<ViewMode>('list');
  const [sectionKeys, setSectionKeys] = useState<Map<string, string>>(() => new Map());
  const [keysReady, setKeysReady] = useState(false);

  // In-card preview playback (cached stems only; one section at a time).
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const engineRef = useRef<StemEngine | null>(null);
  const durationRef = useRef(0);

  useEffect(() => {
    sectionsApi
      .list()
      .then((r) => {
        setRows(r);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    loadSectionIndex()
      .then((m) => {
        setSectionKeys(m);
        setKeysReady(true);
      })
      .catch(() => setKeysReady(true));
  }, []);

  useEffect(() => {
    const eng = new StemEngine();
    eng.setCallbacks({
      onTimeUpdate: (t) => setProgress(durationRef.current > 0 ? Math.min(1, t / durationRef.current) : 0),
      onEnded: () => {
        setIsPlaying(false);
        setProgress(0);
      },
    });
    engineRef.current = eng;
    return () => eng.dispose();
  }, []);

  const items: VM[] = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = rows.filter(
      (s) => !q || s.label.toLowerCase().includes(q) || (s.title || '').toLowerCase().includes(q) || s.video_id.toLowerCase().includes(q) || s.stem.toLowerCase().includes(q),
    );
    const sorters: Record<SortMode, (a: Section, b: Section) => number> = {
      recent: (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      oldest: (a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime(),
      az: (a, b) => songName(a).localeCompare(songName(b)),
      longest: (a, b) => b.end - b.start - (a.end - a.start),
    };
    list = [...list].sort(sorters[sort]);
    return list.map((s) => {
      const mix = mixInfo(s);
      const color = mix.dots[0] ?? STEM_COLOR[s.stem as StemName] ?? 'var(--sec-accent)';
      const stemLabel = STEM_META[s.stem]?.label ?? s.stem;
      return {
        s,
        color,
        tint: color.startsWith('#') ? rgba(color, 0.1) : 'var(--sec-field)',
        stemLabel,
        mix,
        // The section's name (what the user called it) is the headline; the
        // source song is secondary. Un-renamed sections fall back to what's
        // ACTUALLY audible (the mix badge) — NOT s.stem, the originally-picked
        // stem: picking "Vocals" on an instrumental auto-swaps to the loudest
        // stem, so s.stem can read "vocals" while the real content is Solo & Lead.
        name: s.label && s.label.trim() ? s.label : mix.label,
        song: songName(s),
        bars: makeBars(hashSeed(s.id), 56),
        timeRange: `${fmtTime(s.start)}–${fmtTime(s.end)}`,
        clipLen: fmtTime(Math.max(0, s.end - s.start)),
        ago: fmtRelativeDate(s.updated_at),
      };
    });
  }, [rows, query, sort]);

  function mixFor(s: Section): MixState {
    const stems = Object.fromEntries(
      STEMS.map((st) => [st, { volume: s.stem_mix?.[st] ?? (st === (s.stem as StemName) ? 1 : 0), solo: false, mute: false }]),
    ) as MixState['stems'];
    return { ...DEFAULT_MIX, stems, loop: s.loop, speed: 1, transpose: 0, playing: true };
  }

  async function loadAndPlay(s: Section, key: string, frac: number) {
    const eng = engineRef.current;
    if (!eng) return;
    const rec = await getSeparation(key);
    if (!rec) {
      replayInStudio(s);
      return;
    }
    await eng.load(stemUrlsFrom(rec));
    durationRef.current = rec.duration;
    eng.setWindow(0, rec.duration);
    eng.applyMix(mixFor(s));
    if (frac > 0) eng.seekAll(frac * rec.duration);
    await eng.play();
    setPlayingId(s.id);
    setIsPlaying(true);
    setProgress(frac);
  }

  function togglePlay(s: Section) {
    const eng = engineRef.current;
    const key = sectionKeys.get(s.id);
    if (!key) {
      // Audio isn't on this device — go to the Studio to re-separate the source.
      navigate('/');
      return;
    }
    if (playingId === s.id && eng) {
      if (isPlaying) {
        eng.pause();
        setIsPlaying(false);
      } else {
        eng.play();
        setIsPlaying(true);
      }
      return;
    }
    eng?.pause();
    loadAndPlay(s, key, 0);
  }

  function seek(s: Section, e: React.MouseEvent) {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const key = sectionKeys.get(s.id);
    if (!key) {
      navigate('/');
      return;
    }
    if (playingId === s.id && engineRef.current) {
      engineRef.current.seekAll(frac * durationRef.current);
      setProgress(frac);
      if (!isPlaying) {
        engineRef.current.play();
        setIsPlaying(true);
      }
      return;
    }
    engineRef.current?.pause();
    loadAndPlay(s, key, frac);
  }

  async function removeOne(id: string) {
    if (playingId === id) {
      engineRef.current?.pause();
      setIsPlaying(false);
      setPlayingId(null);
    }
    try {
      await sectionsApi.remove(id);
      setRows((r) => r.filter((x) => x.id !== id));
    } catch {
      /* surface later */
    }
  }

  function replayInStudio(s: Section) {
    engineRef.current?.pause();
    navigate('/', { state: { reopen: s } });
  }

  const playingThis = (id: string) => playingId === id && isPlaying;
  const fracFor = (id: string) => (playingId === id ? progress : 0);

  return (
    <div className="sec-root">
      <main className="sec-main">
        <div className="sec-head">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <h1 className="sec-title">Your sections</h1>
              <span className="sec-count">{rows.length}</span>
            </div>
            <p className="sec-sub">Saved stems you've isolated. Press play to preview, or scrub the waveform.</p>
          </div>
          <button className="sec-newbtn" onClick={() => navigate('/')}>
            <Ic.plus /> New section
          </button>
        </div>

        <div className="sec-toolbar">
          <div className="sec-search">
            <Ic.search />
            <input
              className="sec-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by label, song or stem…"
              type="search"
            />
          </div>
          <div className="sec-seg">
            <button className={view === 'grid' ? 'on' : ''} onClick={() => setView('grid')} title="Grid" aria-label="Grid view">
              <Ic.grid />
            </button>
            <button className={view === 'list' ? 'on' : ''} onClick={() => setView('list')} title="List" aria-label="List view">
              <Ic.list />
            </button>
            <button className={view === 'compact' ? 'on' : ''} onClick={() => setView('compact')} title="Compact" aria-label="Compact view">
              <Ic.compact />
            </button>
          </div>
          <div className="sec-sortwrap">
            <select className="sec-select" value={sort} onChange={(e) => setSort(e.target.value as SortMode)}>
              <option value="recent">Most recent</option>
              <option value="oldest">Oldest first</option>
              <option value="az">Song A–Z</option>
              <option value="longest">Longest clip</option>
            </select>
            <Ic.chevron />
          </div>
        </div>

        {!loaded ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <div className="rl-spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : items.length === 0 ? (
          <div className="sec-empty">
            <div className="sec-empty-ic">
              <Ic.note />
            </div>
            <h3 style={{ margin: 0, fontSize: 19, fontWeight: 700 }}>{rows.length === 0 ? 'No sections yet' : 'No matches found'}</h3>
            <p style={{ margin: 0, color: 'var(--sec-sub)', fontSize: 14.5, maxWidth: 340 }}>
              {rows.length === 0
                ? 'Isolate a stem in the Studio and save it here to build your library.'
                : 'Try a different label, song or stem — or clear your search.'}
            </p>
            <button className="sec-newbtn" style={{ marginTop: 14 }} onClick={() => navigate('/')}>
              <Ic.plus /> New section
            </button>
          </div>
        ) : view === 'grid' ? (
          <div className="sec-grid">
            {items.map((vm) => {
              const here = !keysReady || sectionKeys.has(vm.s.id);
              const yt = ytWatchUrl(vm.s);
              return (
                <GridCard key={vm.s.id} vm={vm} here={here} thumbUrl={ytThumb(vm.s)} youtubeUrl={yt} playing={playingThis(vm.s.id)} frac={fracFor(vm.s.id)} onPlay={() => (yt && !here ? openTab(yt) : togglePlay(vm.s))} onSeek={(e) => (yt && !here ? openTab(yt) : seek(vm.s, e))} onReplay={() => (here ? replayInStudio(vm.s) : yt ? openTab(yt) : navigate('/'))} onDelete={() => removeOne(vm.s.id)} progress={progress} />
              );
            })}
          </div>
        ) : view === 'list' ? (
          <div className="sec-list">
            {items.map((vm) => {
              const here = !keysReady || sectionKeys.has(vm.s.id);
              const yt = ytWatchUrl(vm.s);
              return (
                <ListRow key={vm.s.id} vm={vm} here={here} youtubeUrl={yt} playing={playingThis(vm.s.id)} frac={fracFor(vm.s.id)} onPlay={() => (yt && !here ? openTab(yt) : togglePlay(vm.s))} onSeek={(e) => (yt && !here ? openTab(yt) : seek(vm.s, e))} onReplay={() => (here ? replayInStudio(vm.s) : yt ? openTab(yt) : navigate('/'))} onDelete={() => removeOne(vm.s.id)} />
              );
            })}
          </div>
        ) : (
          <div className="sec-compact">
            {items.map((vm) => {
              const here = !keysReady || sectionKeys.has(vm.s.id);
              const yt = ytWatchUrl(vm.s);
              return (
                <CompactRow key={vm.s.id} vm={vm} here={here} youtubeUrl={yt} playing={playingThis(vm.s.id)} onPlay={() => (yt && !here ? openTab(yt) : togglePlay(vm.s))} onReplay={() => (here ? replayInStudio(vm.s) : yt ? openTab(yt) : navigate('/'))} onDelete={() => removeOne(vm.s.id)} />
              );
            })}
          </div>
        )}

        <Footer />
      </main>
    </div>
  );
}

// ── waveform ──
function Wave({ bars, color, frac, onSeek }: { bars: number[]; color: string; frac: number; onSeek: (e: React.MouseEvent) => void }) {
  const n = bars.length;
  return (
    <div className="sec-wave" onClick={onSeek}>
      {bars.map((h, i) => (
        <i key={i} style={{ height: `${h}%`, background: (i + 0.5) / n <= frac ? color : 'var(--sec-wave-mute)' }} />
      ))}
    </div>
  );
}

// Stacked color dots — one per audible stem in the section's mix.
function Dots({ colors }: { colors: string[] }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      {colors.map((c, i) => (
        <span
          key={i}
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: c,
            marginLeft: i ? -3 : 0,
            boxShadow: colors.length > 1 ? '0 0 0 1.5px var(--sec-card)' : 'none',
          }}
        />
      ))}
    </span>
  );
}

interface CardProps {
  vm: VM;
  here: boolean; // are this section's stems cached on this device?
  playing: boolean;
  frac: number;
  onPlay: () => void;
  onSeek: (e: React.MouseEvent) => void;
  onReplay: () => void;
  onDelete: () => void;
  progress?: number;
  thumbUrl?: string | null;   // YouTube thumbnail for url-sourced clips
  youtubeUrl?: string | null; // watch URL — when set + not cached, play opens YouTube
}
// A loop whose audio isn't in THIS browser. Two very different cases, and the old
// single tip gave the wrong advice for the common one: a loop made on YouTube with
// the extension still has its audio — on that video — so opening it there plays it
// for free. Only an upload whose file is gone genuinely needs separating again.
const YT_TIP = 'Audio for this loop lives on the video — open it there to play it';
const GONE_TIP = "Audio isn't on this device — separate the source again to play it";
const offTip = (yt?: string | null) => (yt ? YT_TIP : GONE_TIP);
/** Where a loop can actually be played. null when it plays right here. */
const whereLabel = (here: boolean, yt?: string | null) =>
  here ? 'In Studio' : yt ? 'On YouTube' : 'Not on this device';
const whereTip = (here: boolean, yt?: string | null) =>
  here ? 'Stems are on this device — press play' : offTip(yt);
const whereClass = (here: boolean, yt?: string | null) => (here ? 'studio' : yt ? 'yt' : 'gone');
/** Riffloop's own mark for locally-playable stems: three lanes, like the mixer. */
const StudioMark = ({ size = 12 }: { size?: number } = {}) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="currentColor" aria-hidden focusable="false">
    <rect x="0.5" y="3" width="2" height="6" rx="1" />
    <rect x="5" y="0.5" width="2" height="11" rx="1" />
    <rect x="9.5" y="4.5" width="2" height="3" rx="1" />
  </svg>
);
/** The YouTube mark, at text size — a source badge, so it reads at a glance.
 *  Drawn in the real brand red with a white play triangle, i.e. self-contained:
 *  it stays correct on any chip background (an earlier version punched the
 *  triangle out in the page colour, which only worked on one background). */
const YtMark = ({ size = 14 }: { size?: number } = {}) => (
  <svg width={size} height={size * 0.7} viewBox="0 0 24 17" aria-hidden focusable="false">
    <path
      fill="#FF0000"
      d="M23.5 2.7a3 3 0 0 0-2.1-2.1C19.5 0 12 0 12 0S4.5 0 2.6.6A3 3 0 0 0 .5 2.7 31 31 0 0 0 0 8.5c0 2 .2 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1c.3-1.9.5-3.8.5-5.8s-.2-3.9-.5-5.8Z"
    />
    <path fill="#fff" d="M9.5 12.1 15.8 8.5 9.5 4.9v7.2Z" />
  </svg>
);
/** The "where does this live" chip. When the audio is on a video the chip is a real
 *  <a>, not a button: middle-click, cmd-click and "copy link address" all work,
 *  which a click handler would swallow. Otherwise it is inert status text. */
function WhereChip({ here, yt, small }: { here: boolean; yt?: string | null; small?: boolean }) {
  const cls = `sec-wherechip${small ? ' sm' : ''} ${whereClass(here, yt)}`;
  const body = whereChip(here, yt);
  if (!here && yt) {
    return (
      <a className={`${cls} link`} href={yt} target="_blank" rel="noreferrer" title={`Open the video on YouTube — ${whereTip(here, yt)}`} aria-label="Open on YouTube">
        {body}
      </a>
    );
  }
  return (
    <div className={cls} title={whereTip(here, yt)} aria-label={whereLabel(here, yt)}>
      {body}
    </div>
  );
}

/** Chip contents: a mark reads faster than words once the place is known. */
const whereChip = (here: boolean, yt?: string | null) =>
  here ? (
    <>
      <StudioMark /> Studio
    </>
  ) : yt ? (
    <>
      <YtMark /> YouTube
    </>
  ) : (
    'Not on this device'
  );
/** What the secondary button does for this loop. */
const actionLabel = (here: boolean, yt?: string | null) =>
  here ? 'Replay in Studio' : yt ? 'Open on YouTube' : 'Separate again';

/**
 * The whole row opens the loop — the ↺ alone was a small target for the primary
 * action. Anything interactive inside keeps its own behaviour: the play button, the
 * YouTube link, ✎/✕, and the waveform (which scrubs, and is a div, so it needs
 * naming explicitly rather than relying on `button, a`).
 */
function rowOpensLoop(onOpen: () => void) {
  return (e: React.MouseEvent) => {
    const el = e.target as HTMLElement | null;
    if (el?.closest('button, a, input, .sec-rowwave, .sec-wave, .sec-thumb, .sec-crowwave')) return;
    onOpen();
  };
}

function GridCard({ vm, here, playing, frac, onPlay, onSeek, onReplay, onDelete, progress = 0, thumbUrl, youtubeUrl }: CardProps) {
  const { s } = vm;
  const ytPlay = !here && !!youtubeUrl; // play opens YouTube (can't play locally)
  return (
    <article className="sec-card">
      <div className="sec-thumb" style={{ background: vm.tint }}>
        {thumbUrl ? (
          <>
            <img className="sec-ytthumb" src={thumbUrl} alt="" loading="lazy" onClick={onSeek} title="Open on YouTube" />
            <div className="sec-thumb-ov" />
          </>
        ) : (
          <Wave bars={vm.bars} color={vm.color} frac={frac} onSeek={onSeek} />
        )}
        <div className="sec-badge" style={{ color: vm.mix.textColor }}>
          <Dots colors={vm.mix.dots} />
          {vm.mix.label}
        </div>
        <div className="sec-cliplen">{vm.clipLen}</div>
        <button
          className="sec-play"
          onClick={onPlay}
          aria-label={ytPlay ? 'Open on YouTube' : playing ? 'Pause' : 'Play'}
          style={here || ytPlay ? undefined : { opacity: 0.5 }}
          title={ytPlay ? YT_TIP : here ? undefined : GONE_TIP}
        >
          {playing ? <Ic.pause size={22} /> : <Ic.play size={24} />}
        </button>
        {playing ? (
          <div className="sec-timechip">
            {fmtTime(progress * Math.max(0, s.end - s.start))} / {vm.clipLen}
          </div>
        ) : !here ? (
          // Already inside a ternary branch: this must be a bare expression, not a
          // {…} JSX container.
          youtubeUrl ? (
            <a className="sec-offchip sec-offchip-yt link" href={youtubeUrl} target="_blank" rel="noreferrer" title={`Open the video on YouTube — ${YT_TIP}`} aria-label="Open on YouTube">
              {whereChip(false, youtubeUrl)}
            </a>
          ) : (
            <div className="sec-offchip" title={GONE_TIP}>
              {whereChip(false, null)}
            </div>
          )
        ) : null}
      </div>
      <div className="sec-body">
        <h3 className="sec-song">{vm.name}</h3>
        <p className="sec-artist">{vm.song}</p>
        <div className="sec-meta">
          <Ic.clock />
          <span>{vm.timeRange}</span>
          <span style={{ opacity: 0.5 }}>·</span>
          <span>{vm.ago}</span>
        </div>
        <div className="sec-cardfoot">
          <button className="sec-replay" onClick={onReplay} title={here ? undefined : offTip(youtubeUrl)}>
            <Ic.replay /> {actionLabel(here, youtubeUrl)}
          </button>
          <button className="sec-iconbtn sec-del" onClick={onDelete} aria-label="Delete section">
            <Ic.x />
          </button>
        </div>
      </div>
    </article>
  );
}

function ListRow({ vm, here, playing, frac, onPlay, onSeek, onReplay, onDelete, youtubeUrl }: CardProps) {
  const ytPlay = !here && !!youtubeUrl;
  return (
    <div className="sec-row sec-row-open" onClick={rowOpensLoop(onReplay)} title="Open this loop">
      <button className="sec-rowplay" onClick={onPlay} aria-label={ytPlay ? 'Open on YouTube' : playing ? 'Pause' : 'Play'} style={here || ytPlay ? undefined : { opacity: 0.5 }} title={ytPlay ? YT_TIP : here ? undefined : GONE_TIP}>
        {playing ? <Ic.pause size={18} /> : <Ic.play size={20} />}
      </button>
      <div className="sec-pill" style={{ background: vm.tint, color: vm.mix.textColor }}>
        <Dots colors={vm.mix.dots} />
        {vm.mix.label}
      </div>
      <div className="sec-rowmeta">
        <b>{vm.name}</b>
        <span>
          {vm.song}
        </span>
      </div>
      <WhereChip here={here} yt={youtubeUrl} />
      <div className="sec-rowwave" style={{ background: vm.tint }}>
        <Wave bars={vm.bars} color={vm.color} frac={frac} onSeek={onSeek} />
      </div>
      <div className="sec-rowtime">
        <b>{vm.timeRange}</b>
        <span>{vm.ago}</span>
      </div>
      <div className="sec-rowact">
        <button className="sec-iconbtn-lg" onClick={onReplay} aria-label={actionLabel(here, youtubeUrl)} title={here ? 'Replay in Studio' : offTip(youtubeUrl)}>
          <Ic.replay />
        </button>
        <button className="sec-iconbtn-lg sec-del" onClick={onDelete} aria-label="Delete section">
          <Ic.x />
        </button>
      </div>
    </div>
  );
}

function CompactRow({ vm, here, playing, onPlay, onReplay, onDelete, youtubeUrl }: Omit<CardProps, 'frac' | 'onSeek'>) {
  const ytPlay = !here && !!youtubeUrl;
  return (
    <div className="sec-crow sec-row-open" onClick={rowOpensLoop(onReplay)} title="Open this loop">
      <button className="sec-crowplay" onClick={onPlay} aria-label={ytPlay ? 'Open on YouTube' : playing ? 'Pause' : 'Play'} style={here || ytPlay ? undefined : { opacity: 0.5 }} title={ytPlay ? YT_TIP : here ? undefined : GONE_TIP}>
        {playing ? <Ic.pause size={13} /> : <Ic.play size={15} />}
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flex: 'none', width: 124 }}>
        <Dots colors={vm.mix.dots} />
        <span style={{ fontSize: 13, fontWeight: 700, color: vm.mix.textColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{vm.mix.label}</span>
      </div>
      <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 9, whiteSpace: 'nowrap', overflow: 'hidden' }}>
        <span style={{ fontSize: 14.5, fontWeight: 700, letterSpacing: '-0.2px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{vm.name}</span>
        <span style={{ color: 'var(--sec-sub)', fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {vm.song}
        </span>
      </div>
      <WhereChip here={here} yt={youtubeUrl} small />
      <span style={{ flex: 'none', color: 'var(--sec-sub)', fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{vm.timeRange}</span>
      <span style={{ flex: 'none', color: 'var(--sec-sub)', fontSize: 12.5, fontWeight: 600, minWidth: 62, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{vm.ago}</span>
      <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
        <button className="sec-iconbtn-sm" onClick={onReplay} aria-label={actionLabel(here, youtubeUrl)} title={here ? 'Replay in Studio' : offTip(youtubeUrl)}>
          <Ic.replay size={15} />
        </button>
        <button className="sec-iconbtn-sm sec-del" onClick={onDelete} aria-label="Delete section">
          <Ic.x size={15} />
        </button>
      </div>
    </div>
  );
}

// ── icons ──
const Ic = {
  plus: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  search: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--sec-sub)" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.2-3.2" />
    </svg>
  ),
  grid: () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
      <rect x="3" y="3" width="8" height="8" rx="1.8" />
      <rect x="13" y="3" width="8" height="8" rx="1.8" />
      <rect x="3" y="13" width="8" height="8" rx="1.8" />
      <rect x="13" y="13" width="8" height="8" rx="1.8" />
    </svg>
  ),
  list: () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
      <path d="M8.5 6h12M8.5 12h12M8.5 18h12" />
      <circle cx="3.6" cy="6" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="3.6" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="3.6" cy="18" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  ),
  compact: () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
      <path d="M3 5h18M3 9.5h18M3 14.5h18M3 19h18" />
    </svg>
  ),
  chevron: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--sec-sub)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 9 6 6 6-6" />
    </svg>
  ),
  play: ({ size = 24 }: { size?: number } = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5.2v13.6a1 1 0 0 0 1.52.86l11-6.8a1 1 0 0 0 0-1.72l-11-6.8A1 1 0 0 0 8 5.2z" />
    </svg>
  ),
  pause: ({ size = 22 }: { size?: number } = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="5" width="4" height="14" rx="1.2" />
      <rect x="14" y="5" width="4" height="14" rx="1.2" />
    </svg>
  ),
  clock: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" strokeLinecap="round" />
    </svg>
  ),
  replay: ({ size = 15 }: { size?: number } = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  ),
  x: ({ size = 17 }: { size?: number } = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  ),
  note: () => (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  ),
};
